import type {
	QcFieldMeta,
	QcFieldMetaWithOptions,
	QcCustomerDetailResponse,
	QcCustomerAction,
	SaveQcCustomerRequest,
} from '@/libs/fleet-api/services/qc-customer-types';
import type {
	QcPdMainDetailResponse,
	QcPdApplicationDetailResponse,
	QcPdAssetDetailResponse,
	QcPdAssetDetailMeta,
	QcPdFinancialDetailResponse,
	QcPdInsuranceDetailResponse,
	QcPdIncentiveDetailResponse,
	QcPdInsurancePaymentScheduleItem,
	DefaultPairResponse,
	FileResponse,
	SaveQcPdApplicationRequest,
	SaveQcPdAssetRequest,
	SaveQcPdFinancialRequest,
	SaveQcPdInsuranceRequest,
	SaveQcPdIncentiveRequest,
	QcIncentiveDistributionItemResponse,
	QcApplicationAction,
} from '@/libs/fleet-api/services/qc-pra-disbursement-types';
import type {
	FieldConfig,
	AccordionSection,
} from '@/pages/robotic/components/QCAccordionForm';
import dayjs from 'dayjs';

// ─── Types ──────────────────────────────────────────────────────────────────

export type FieldMetaEntry = {
	editable: boolean;
	required: boolean;
	options?: { label: string; value: string }[];
	sourceEndpoint?: string;
};

export type FieldMetaMap = Record<string, FieldMetaEntry>;

type MappingResult = {
	values: Record<string, unknown>;
	fieldMeta: FieldMetaMap;
};

// ─── Apply API metadata to FieldConfig ──────────────────────────────────────

const applyMetaToField = (
	field: FieldConfig,
	meta: FieldMetaMap,
): FieldConfig => {
	const m = meta[field.name];
	if (!m) return field;
	return {
		...field,
		disabled: !m.editable,
		required: m.required,
		...(m.options?.length ? { options: m.options } : {}),
		...(m.sourceEndpoint ? { sourceEndpoint: m.sourceEndpoint } : {}),
	};
};

export const applyFieldMeta = (
	fields: FieldConfig[],
	meta: FieldMetaMap,
): FieldConfig[] => fields.map((f) => applyMetaToField(f, meta));

export const applySectionsMeta = (
	sections: AccordionSection[],
	meta: FieldMetaMap,
): AccordionSection[] =>
	sections.map((s) => ({
		...s,
		fields: s.fields ? applyFieldMeta(s.fields, meta) : s.fields,
	}));

// ─── Helpers ────────────────────────────────────────────────────────────────

const extractMeta = (
	field: QcFieldMeta<unknown> & { sourceEndpoint?: string | null },
): FieldMetaEntry => ({
	editable: field.editable,
	required: field.required,
	...(field.sourceEndpoint ? { sourceEndpoint: field.sourceEndpoint } : {}),
});

const mapOptionsToSelect = (field: QcFieldMetaWithOptions<unknown>) =>
	(field.options ?? []).map((o) => ({
		label: o.value ?? '',
		value: String(o.id ?? ''),
	}));

// Pass-through select value mapper: keep raw API value (string) so submit preserves it.
// Display layer is responsible for blanking when value doesn't match any option.
const rawSelectValue = (
	field: QcFieldMetaWithOptions<number | null> | null | undefined,
): string | undefined =>
	field?.value != null ? String(field.value) : undefined;

const mapDefaultPairToSelect = (
	pairs: DefaultPairResponse[] | null | undefined,
) =>
	(pairs ?? []).map((o) => ({
		label: o.value ?? '',
		value: String(o.id ?? ''),
	}));

const boolToRadio = (val: boolean | null | undefined): string | undefined => {
	if (val === true) return 'yes';
	if (val === false) return 'no';
	return undefined;
};

const radioToBool = (val: string | undefined): boolean | null => {
	if (val === 'yes') return true;
	if (val === 'no') return false;
	return null;
};

// ─── Shared setter helpers ────────────────────────────────────────────────

const createSetters = (
	values: Record<string, unknown>,
	fieldMeta: FieldMetaMap,
) => {
	const setStr = (name: string, field?: QcFieldMeta<string | null> | null) => {
		if (!field) return;
		values[name] = field.value ?? '';
		fieldMeta[name] = extractMeta(field);
	};

	const setNum = (name: string, field?: QcFieldMeta<number | null> | null) => {
		if (!field) return;
		values[name] = field.value;
		fieldMeta[name] = extractMeta(field);
	};

	const setBool = (
		name: string,
		field?: QcFieldMeta<boolean | null> | null,
	) => {
		if (!field) return;
		values[name] = boolToRadio(field.value);
		fieldMeta[name] = extractMeta(field);
	};

	const setSelect = (
		name: string,
		field?: QcFieldMetaWithOptions<number | null> | null,
	) => {
		if (!field) return;
		// Keep raw value so submit preserves it. Display layer (Select/table) blanks if no matching option.
		values[name] = field.value != null ? String(field.value) : undefined;
		const opts = mapOptionsToSelect(field);
		fieldMeta[name] = {
			...extractMeta(field),
			...(opts.length ? { options: opts } : {}),
		};
	};

	const setDate = (name: string, field?: QcFieldMeta<string | null> | null) => {
		if (!field) return;
		values[name] = field.value ? dayjs(field.value) : undefined;
		fieldMeta[name] = extractMeta(field);
	};

	// For string-valued selects where options use the display value as the submit value
	const setSelectStr = (
		name: string,
		field?: QcFieldMetaWithOptions<string | null> | null,
	) => {
		if (!field) return;
		values[name] = field.value ?? undefined;
		const opts = (field.options ?? []).map((o) => ({
			label: o.value ?? '',
			value: o.value ?? '',
		}));
		fieldMeta[name] = {
			editable: field.editable,
			required: field.required,
			...(opts.length ? { options: opts } : {}),
		};
	};

	return { setStr, setNum, setBool, setSelect, setDate, setSelectStr };
};

// ─── Shared value extractors for save requests ──────────────────────────────

const str = (values: Record<string, unknown>, key: string) =>
	(values[key] as string) || null;
const num = (values: Record<string, unknown>, key: string) => {
	const v = values[key];
	return v != null && v !== '' ? Number(v) : null;
};
const int = (values: Record<string, unknown>, key: string) => {
	const v = values[key];
	return v != null && v !== '' ? parseInt(String(v), 10) : null;
};
const bool = (values: Record<string, unknown>, key: string) =>
	radioToBool(values[key] as string | undefined);
const dateStr = (values: Record<string, unknown>, key: string) => {
	const v = values[key];
	if (!v) return null;
	if (typeof v === 'object' && 'format' in (v as object)) {
		return (v as dayjs.Dayjs).format('YYYY-MM-DD');
	}
	return String(v);
};

// ═══════════════════════════════════════════════════════════════════════════
// MAIN DETAIL → DataRow info
// ═══════════════════════════════════════════════════════════════════════════

export type QcPdMainInfoMapped = {
	customerName: string;
	customerId: string;
	purchaseOrderId: number | null;
	purchaseOrderNo: string;
	frmName: string;
	fbdhName: string;
	napNo: string;
	napId: number | null;
	napResolutionId: number | null;
	prospectId: string;
	sourceOrder: string;
	spfNo: string;
	spfId: number | null;
	facilityType: string;
	mklNo: string;
	asset: string;
	simulationNo: string;
	simulationId: number | null;
	rkpDate: string;
	mppNo: string;
	mppId: number | null;
	resolutionFullApprovedDate: string;
	financingAgreementNo: string;
	agreementPrintingId: number | null;
	financingProductId: number | null;
	groupBmppName: string;
	groupBmppId: string;
	disbursementNo: string;
	disbursementId: number | null;
	resolutionSummaryDocument: FileResponse | null;
};

export const mapMainDetailToInfo = (
	response: QcPdMainDetailResponse,
): QcPdMainInfoMapped => {
	const m = response.mainInformation;
	return {
		customerName: m?.customerName ?? '',
		customerId: m?.customerId ?? '',
		purchaseOrderId: m?.purchaseOrderId ?? null,
		purchaseOrderNo: m?.purchaseOrderNo ?? '',
		frmName: m?.frmName ?? '',
		fbdhName: m?.fbdhName ?? '',
		napNo: m?.napNo ?? '',
		napId: m?.napId ?? null,
		napResolutionId: m?.napResolutionId ?? null,
		prospectId: m?.prospectId ?? '',
		sourceOrder: m?.sourceOrder ?? '',
		spfNo: m?.spfNo ?? '',
		spfId: m?.spfId ?? null,
		facilityType: m?.facilityType ?? '',
		mklNo: m?.mklNo ?? '',
		asset: m?.asset ?? '',
		simulationNo: m?.simulationNo ?? '',
		simulationId: m?.simulationId ?? null,
		rkpDate: m?.rkpDate ?? '',
		mppNo: m?.mppNo ?? '',
		mppId: m?.mppId ?? null,
		resolutionFullApprovedDate: m?.resolutionFullApprovedDate ?? '',
		financingAgreementNo: m?.financingAgreementNo ?? '',
		agreementPrintingId: m?.agreementPrintingId ?? null,
		financingProductId: m?.financingProductId ?? null,
		groupBmppName: m?.groupBmppName ?? '',
		groupBmppId: m?.groupBmppId ?? '',
		disbursementNo: m?.disbursementNo ?? '',
		disbursementId: m?.disbursementId ?? null,
		resolutionSummaryDocument: m?.resolutionSummaryDocument ?? null,
	};
};

// ═══════════════════════════════════════════════════════════════════════════
// TAB 1: CUSTOMER DETAIL → Form Values
// Reuses the same response type as QC New Customer
// ═══════════════════════════════════════════════════════════════════════════

export const mapCustomerDetailToForm = (
	detail: QcCustomerDetailResponse,
): MappingResult => {
	const values: Record<string, unknown> = {};
	const fieldMeta: FieldMetaMap = {};
	const { setStr, setNum, setBool, setSelect, setDate } = createSetters(
		values,
		fieldMeta,
	);

	// Customer Data (top fields)
	const cd = detail.customerData;
	if (cd) {
		setStr('namaSesuaiAkta', cd.legalName);
		setStr('npwp', cd.npwpNo);
		setStr('namaDataBank', cd.nameForBank);
		setStr('alamatLengkap', cd.address);
		setStr('rt', cd.rt);
		setStr('rw', cd.rw);
		setStr('kodePos', cd.postalCode);
		setStr('kelurahan', cd.subDistrict);
		setStr('kecamatan', cd.district);
		setStr('kota', cd.city);
		setStr('noTelepon', cd.phoneNo);
	}

	// Company Data
	const cp = detail.companyData;
	if (cp) {
		setStr('tempatAktaDiterbitkan', cp.deedIssuedPlace);
		setStr('tahunPendirian', cp.deedFoundingYear);
		setStr('jenisPerusahaan', cp.companyType);
		setStr('emailPerusahaan', cp.companyEmail);
		setNum('jumlahKaryawan', cp.numberOfEmployees);
		setStr('groupBmpp', cp.groupBmppName);
		setSelect('targetMarket', cp.targetMarket);
		setBool('goPublic', cp.isGoPublic);
		setSelect('statusPerusahaan', cp.companyStatus);
	}

	// Legal Address
	const la = detail.legalAddress;
	if (la) {
		setStr('alamatLengkapAkta', la.address);
		setStr('rtAkta', la.rt);
		setStr('rwAkta', la.rw);
		values['addressAkta'] = {
			postalId: 0,
			postalCode: la.postalCode?.value ?? '',
			locationName: la.subDistrict?.value ?? '',
			districtName: la.district?.value ?? '',
			cityName: la.city?.value ?? '',
		};
		// addressAkta meta from postalCode (the editable picker in FE)
		if (la.postalCode) fieldMeta['addressAkta'] = extractMeta(la.postalCode);
		// Display-only address fields (kelurahan, kecamatan, kota) — meta from API
		if (la.subDistrict)
			fieldMeta['kelurahanAkta'] = extractMeta(la.subDistrict);
		if (la.district) fieldMeta['kecamatanAkta'] = extractMeta(la.district);
		if (la.city) fieldMeta['kotaAkta'] = extractMeta(la.city);
		setStr('noTeleponAkta', la.phoneNo);
		setStr('noHandphoneAkta', la.mobileNo);
	}

	// Operational Address
	const oa = detail.operationalAddress;
	if (oa) {
		setBool('useAlamatLegal', oa.useSameAsLegalAddress);
		setStr('alamatLengkapOperasional', oa.address);
		setStr('rtOperasional', oa.rt);
		setStr('rwOperasional', oa.rw);
		values['addressOperasional'] = {
			postalId: 0,
			postalCode: oa.postalCode?.value ?? '',
			locationName: oa.subDistrict?.value ?? '',
			districtName: oa.district?.value ?? '',
			cityName: oa.city?.value ?? '',
		};
		if (oa.postalCode)
			fieldMeta['addressOperasional'] = extractMeta(oa.postalCode);
		if (oa.subDistrict)
			fieldMeta['kelurahanOperasional'] = extractMeta(oa.subDistrict);
		if (oa.district)
			fieldMeta['kecamatanOperasional'] = extractMeta(oa.district);
		if (oa.city) fieldMeta['kotaOperasional'] = extractMeta(oa.city);
		setStr('noTeleponOperasional', oa.phoneNo);
	}

	// Financial Data
	const fd = detail.financialData;
	if (fd) {
		setNum('penghasilanPerBulan', fd.monthlyIncomeAmount);
		setNum('rasioLancar', fd.currentRatioPercentage);
		setNum('tingkatRoi', fd.returnOnInvestment);
		setNum('rasioAngsuranPenghasilan', fd.installmentToIncomeRatioPercentage);
		setSelect('jenisRekeningBank', fd.bankAccountType);
		setNum('rataRataSaldoAkhir', fd.averageEndingBalanceAmount);
		setNum('rataRataSaldoMengendap', fd.averageDepositBalanceAmount);
		setNum('kasSetaraKas', fd.cashAndCashEquivalentAmount);
		setNum('piutangUsahaLancar', fd.tradeReceivableCurrentAssetAmount);
		setNum(
			'investasiKeuanganLancar',
			fd.investmentOtherFinancialCurrentAssetAmount,
		);
		setNum('asetLancarLainnya', fd.otherCurrentAssetAmount);
		setNum('totalAsetLancar', fd.totalCurrentAssetAmount);
		setNum('piutangUsahaTidakLancar', fd.tradeReceivableNonCurrentAssetAmount);
		setNum(
			'investasiKeuanganTidakLancar',
			fd.investmentOtherFinancialNonCurrentAssetAmount,
		);
		setNum('asetTidakLancarLainnya', fd.otherNonCurrentAssetAmount);
		setNum('totalAsetTidakLancar', fd.totalNonCurrentAssetAmount);
		setNum('totalAset', fd.totalAssetAmount);
		setSelect('statusTempatUsaha', fd.businessPlaceOwnershipStatus);
		setStr('menempatiTempatUsahaSejak', fd.occupiedSinceYear);
		setDate('tanggalBerakhirnyaSewa', fd.leaseEndDate);
		setStr('tahunLaporan', fd.reportYear);
		setNum('aktivaLancar', fd.currentAssetNonFixedAssetAmount);
		setNum('totalHutang', fd.totalDebtAmount);
		setNum('hutangJangkaPendek', fd.shortTermDebtAmount);
		setNum('modalDisetor', fd.paidInCapitalAmount);
		setNum('nilaiPenjualan', fd.salesValueAmount);
		setNum('pendapatanOperasional', fd.operationalIncomeAmount);
		setNum('labaRugiBruto', fd.grossProfitLossAmount);
		setNum('pendapatanNonOperasional', fd.nonOperationalIncomeAmount);
		setNum('labaRugiSebelumPajak', fd.profitLossBeforeTaxAmount);
		setNum('labaRugiTahunBerjalan', fd.currentYearProfitLossAmount);
		setBool('statusAudit', fd.isAudited);
		setStr('diAuditOleh', fd.auditedBy);
		setNum('pinjamanJangkaPendek', fd.shortTermLoanLiabilityAmount);
		setNum('utangUsahaJangkaPendek', fd.tradePayableShortTermLiabilityAmount);
		setNum('liabilitasJangkaPendekLainnya', fd.otherShortTermLiabilityAmount);
		setNum('liabilitasJangkaPendek', fd.totalShortTermLiabilityAmount);
		setNum('pinjamanJangkaPanjang', fd.longTermLoanLiabilityAmount);
		setNum('utangUsahaJangkaPanjang', fd.tradePayableLongTermLiabilityAmount);
		setNum('liabilitasJangkaPanjangLainnya', fd.otherLongTermLiabilityAmount);
		setNum('liabilitasJangkaPanjang', fd.totalLongTermLiabilityAmount);
		setNum('liabilitas', fd.totalLiabilityAmount);
		setNum('ekuitas', fd.equityAmount);
		setNum('totalPassiva', fd.totalPassiveAmount);
	}

	// Warehouse
	const wh = detail.warehouseOfficeWorkshop;
	if (wh) {
		setSelect('statusKepemilikanGudang', wh.ownershipStatus);
		setStr('alamatGudang', wh.address);
		setStr('rtGudang', wh.rt);
		setStr('rwGudang', wh.rw);
		setStr('kodeposGudang', wh.postalCode);
		setStr('kelurahanGudang', wh.subDistrict);
		setStr('kecamatanGudang', wh.district);
		setStr('kotaGudang', wh.city);
		setStr('noTeleponGudang', wh.phoneNo);
	}

	// Bank Account
	const ba = detail.bankAccount;
	if (ba) {
		setStr('namaBank', ba.bankName);
		setStr('namaCabangBank', ba.branchName);
		setStr('nomorRekening', ba.accountNumber);
	}

	// Other Data
	const od = detail.otherData;
	if (od) {
		setBool(
			'setujuPemasaranPihakKetiga',
			od.isAgreeThirdPartyMarketingPersonalComm,
		);
		setBool('pernahKreditMobil', od.hasAppliedForVehicleCredit);
		setBool('daftarHitamAppi', od.isOnAppiBlacklist);
		setBool('setujuPemrosesan', od.isAgreeDataProcessingForFinancing);
		setBool('setujuPemasaranMtf', od.isAgreeMtfMarketingPersonalComm);
	}

	// PIC Document Signer
	const pic = detail.picDocumentSigner;
	if (pic) {
		setStr('namaPicTtd', pic.name);
		setStr('alamatLengkapPicTtd', pic.address);
		setStr('noKtpPicTtd', pic.idNumber);
		setStr('jabatanPicTtd', pic.position);
	}

	// PIC Billing
	const pb = detail.picBilling;
	if (pb) {
		setStr('namaPicPenagihan', pb.name);
		setStr('jabatanPicPenagihan', pb.position);
		setStr('noTeleponPicPenagihan', pb.phoneNumber);
		setStr('noTelepon2PicPenagihan', pb.phoneNumber2);
	}

	// Management Shareholders — per-row meta stored alongside data
	if (detail.managementShareholders?.length) {
		values['sahamData'] = detail.managementShareholders.map((sh) => ({
			nama: sh.name?.value ?? '',
			kewarganegaraan: rawSelectValue(sh.citizenship),
			kebangsaan: sh.nationality?.value ?? '',
			jenisIdentitas: rawSelectValue(sh.identityCardType),
			noIdentitas: sh.identityNumber?.value ?? '',
			tanggalLahir: sh.dateOfBirth?.value
				? dayjs(sh.dateOfBirth.value)
				: undefined,
			jenisKelamin: rawSelectValue(sh.gender),
			noTelepon: sh.phoneNumber?.value ?? '',
			jabatan: rawSelectValue(sh.position),
			alamat: sh.address?.value ?? '',
			kodePos: sh.postalCode?.value ?? '',
			kelurahan: sh.subDistrict?.value ?? '',
			kecamatan: sh.district?.value ?? '',
			kota: sh.city?.value ?? '',
			rt: sh.rt?.value ?? '',
			rw: sh.rw?.value ?? '',
			status: rawSelectValue(sh.status),
			isShareholder: sh.isShareholder?.value,
			persentaseKepemilikan: sh.shareholding?.value,
			// identityFile is read-only from API
			identityFile: sh.identityFile
				? {
						id: sh.identityFile.id,
						name: sh.identityFile.name,
						url: (sh.identityFile as { value?: string }).value,
					}
				: undefined,
		}));
		// Per-row meta — keyed by FE field name in the modal
		values['sahamDataMeta'] = detail.managementShareholders.map((sh) => {
			const m: FieldMetaMap = {};
			if (sh.name) m['nama'] = extractMeta(sh.name);
			if (sh.citizenship)
				m['kewarganegaraan'] = {
					...extractMeta(sh.citizenship),
					options: mapOptionsToSelect(sh.citizenship),
				};
			if (sh.nationality) m['nationality'] = extractMeta(sh.nationality);
			if (sh.identityCardType)
				m['jenisIdentitas'] = {
					...extractMeta(sh.identityCardType),
					options: mapOptionsToSelect(sh.identityCardType),
				};
			if (sh.identityNumber) m['noId'] = extractMeta(sh.identityNumber);
			if (sh.identityFile) m['identityFile'] = extractMeta(sh.identityFile);
			if (sh.dateOfBirth) m['tanggalLahir'] = extractMeta(sh.dateOfBirth);
			if (sh.gender)
				m['jenisKelamin'] = {
					...extractMeta(sh.gender),
					options: mapOptionsToSelect(sh.gender),
				};
			if (sh.phoneNumber) m['noTelepon'] = extractMeta(sh.phoneNumber);
			if (sh.position)
				m['jabatan'] = {
					...extractMeta(sh.position),
					options: mapOptionsToSelect(sh.position),
				};
			if (sh.address) m['alamat'] = extractMeta(sh.address);
			if (sh.postalCode) m['kodePos'] = extractMeta(sh.postalCode);
			if (sh.subDistrict) m['kelurahan'] = extractMeta(sh.subDistrict);
			if (sh.district) m['kecamatan'] = extractMeta(sh.district);
			if (sh.city) m['kota'] = extractMeta(sh.city);
			if (sh.rt) m['rt'] = extractMeta(sh.rt);
			if (sh.rw) m['rw'] = extractMeta(sh.rw);
			if (sh.status)
				m['status'] = {
					...extractMeta(sh.status),
					options: mapOptionsToSelect(sh.status),
				};
			if (sh.isShareholder) m['isManagement'] = extractMeta(sh.isShareholder);
			if (sh.shareholding) m['kepemilikanSaham'] = extractMeta(sh.shareholding);
			return m;
		});
	}

	// Legal Documents — always set from API (empty array = no documents yet)
	values['legalitasDokumen'] = (detail.legalDocuments ?? []).map((doc) => ({
		jenis: doc.documentType?.value ?? '',
		noDokumen: doc.documentNumber?.value ?? '',
		tanggalDokumen: doc.documentDate?.value
			? dayjs(doc.documentDate.value)
			: null,
		notes: doc.notes?.value ?? '',
	}));

	return { values, fieldMeta };
};

// ═══════════════════════════════════════════════════════════════════════════
// TAB 2: APPLICATION DETAIL → Form Values
// ═══════════════════════════════════════════════════════════════════════════

export type ApplicationMappingResult = MappingResult & {
	beneficialOwnersData: Record<string, unknown>[];
	beneficialOwnerOptions: {
		relationship: { label: string; value: string }[];
		nationality: { label: string; value: string }[];
		gender: { label: string; value: string }[];
		maritalStatus: { label: string; value: string }[];
	};
};

export const mapApplicationDetailToForm = (
	detail: QcPdApplicationDetailResponse,
): ApplicationMappingResult => {
	const values: Record<string, unknown> = {};
	const fieldMeta: FieldMetaMap = {};
	const { setStr, setNum, setBool, setSelect, setDate } = createSetters(
		values,
		fieldMeta,
	);

	// Product Information (top fields)
	const pi = detail.productInformation;
	if (pi) {
		setStr('productCustomerType', pi.productCustomerType);
		setStr('mandiriKpm', pi.mandiriKpm);
		setStr('jenisAssetApp', pi.assetType);
		setStr('angsuranPertama', pi.firstInstallment);
		setSelect('spesifikProduct', pi.specificProduct);
		setStr('produk', pi.product);
		setStr('kondisiKendaraan', pi.vehicleCondition);
		setNum('tenorApp', pi.tenor);
		setStr('productOffering', pi.productOffering);
		setNum('tahunPerakitan', pi.assemblyYear);
		setStr('mklNo', pi.mklNo);
		setStr('poNumber', pi.poNumber);
		setSelect('installmentScheme', pi.installmentScheme);
		setStr('interestRate', pi.interestRate);
		setBool('multipleAgreement', pi.multipleAgreement);
		setNum('copyAgreement', pi.copyAgreement);
	}

	// Entry Application Information
	const eai = detail.entryApplicationInformation;
	if (eai) {
		setStr('jenisPembayaran', eai.paymentType);
		setDate('tanggalSurvey', eai.surveyDate);
		setStr('sumberAplikasi', eai.applicationSource);
	}

	// Bank Account
	const ba = detail.bankAccount;
	if (ba) {
		setStr('namaBankApp', ba.bankName);
		setStr('bankBranch', ba.bankBranch);
		setStr('accountNo', ba.accountNumber);
	}

	// MTF Employee
	const me = detail.mtfEmployee;
	if (me) {
		setStr('accountOfficer', me.accountOfficer);
		setStr('creditAnalyst', me.creditAnalyst);
		setStr('surveyor', me.surveyor);
		setNum('fiducia', me.fiduciaryAmount);
		setNum('admin', me.adminAmount);
		setNum('provisi', me.provisionAmount);
		setNum('serviceFee', me.serviceFeeAmount);
		setNum('refundAdministration', me.refundAdministrationAmount);
		setNum('refundProvisi', me.refundProvisionAmount);
	}

	// Non-Resident Reference
	const nr = detail.nonResidentReference;
	if (nr) {
		setStr('namaReferensi', nr.name);
		setStr('hubunganReferensi', nr.relationshipWithCustomer);
		setStr('alamatLengkapReferensi', nr.fullAddress);
		setStr('rtReferensi', nr.rt);
		setStr('rwReferensi', nr.rw);
		setStr('kelurahanReferensi', nr.kelurahan);
		setStr('kodePosReferensi', nr.postalCode);
		setStr('kecamatanReferensi', nr.kecamatan);
		setStr('noTeleponReferensi', nr.phoneNumber);
		setStr('kotaReferensi', nr.city);
	}

	// Environment Survey
	const es = detail.environmentSurvey;
	if (es) {
		setStr('namaSumberInformasi', es.informationSourceName);
		setStr('hubunganSurvey', es.relationshipWithCustomer);
		setStr('noHandphoneSurvey', es.mobilePhone);
		setSelect('karakterCustomer', es.customerCharacter);
	}

	// Mailing Address
	const ma = detail.mailingAddress;
	if (ma) {
		setSelect('salinAlamatDari', ma.copyAddressFrom);
		setStr('alamatLengkapMailing', ma.fullAddress);
		setStr('rtMailing', ma.rt);
		setStr('rwMailing', ma.rw);
		setStr('kelurahanMailing', ma.kelurahan);
		setStr('kodePosMailing', ma.postalCode);
		setStr('kecamatanMailing', ma.kecamatan);
		setStr('noTeleponMailing', ma.phoneNumber);
		setStr('kotaMailing', ma.city);
		setNum('jarakDariKantorMtf', ma.distanceFromMtfOfficeKm);
	}

	// ── Hubungan Dengan Customer options ────────────────────────────────────
	// BE returns relationship as plain string for Referensi & Survey; reuse the top-level
	// `relationshipWithCustomerOptions` (same source as Pemilik Manfaat) so the Select renders.
	const relationshipOptions = mapDefaultPairToSelect(detail.relationshipWithCustomerOptions);
	if (relationshipOptions.length) {
		if (fieldMeta['hubunganReferensi']) {
			fieldMeta['hubunganReferensi'] = {
				...fieldMeta['hubunganReferensi'],
				options: relationshipOptions,
			};
		}
		if (fieldMeta['hubunganSurvey']) {
			fieldMeta['hubunganSurvey'] = {
				...fieldMeta['hubunganSurvey'],
				options: relationshipOptions,
			};
		}
	}

	// ── Kode Pos lookup endpoints ───────────────────────────────────────────
	// Make Kode Pos searchable via /api/master-address/get-list. Render layer transforms
	// `text` field with sourceEndpoint into LookupSelect automatically.
	const POSTAL_ENDPOINT = '/api/master-address/get-list';
	if (fieldMeta['kodePosReferensi']) {
		fieldMeta['kodePosReferensi'] = {
			...fieldMeta['kodePosReferensi'],
			sourceEndpoint: POSTAL_ENDPOINT,
		};
	}
	if (fieldMeta['kodePosMailing']) {
		fieldMeta['kodePosMailing'] = {
			...fieldMeta['kodePosMailing'],
			sourceEndpoint: POSTAL_ENDPOINT,
		};
	}

	// Beneficial Owners (table data)
	const beneficialOwnersData = (detail.beneficialOwners ?? []).map(
		(bo, index) => ({
			key: index,
			id: bo.id,
			hubunganDenganCustomer:
				bo.relationship?.value != null ? String(bo.relationship.value) : '',
			nama: bo.name?.value ?? '',
			nik: bo.nik?.value ?? '',
			npwp: bo.npwp?.value ?? '',
			tempatLahir: bo.birthPlace?.value ?? '',
			tanggalLahir: bo.birthDate?.value ? dayjs(bo.birthDate.value) : null,
			kewarganegaraan:
				bo.nationality?.value != null ? String(bo.nationality.value) : '',
			negaraAsal: bo.originCountry?.value ?? '',
			jenisKelamin: bo.gender?.value != null ? String(bo.gender.value) : '',
			statusPerkawinan:
				bo.maritalStatus?.value != null ? String(bo.maritalStatus.value) : '',
			kodePos: bo.postalCode?.value ?? '',
			kelurahan: bo.subDistrict?.value ?? '',
			kecamatan: bo.district?.value ?? '',
			kota: bo.city?.value ?? '',
			jenisPekerjaan: bo.occupation?.value ?? '',
			namaPerusahaan: bo.companyOrBusinessName?.value ?? '',
		}),
	);

	// Options arrays
	const beneficialOwnerOptions = {
		relationship: mapDefaultPairToSelect(
			detail.relationshipWithCustomerOptions,
		),
		nationality: mapDefaultPairToSelect(detail.nationalityOptions),
		gender: mapDefaultPairToSelect(detail.genderOptions),
		maritalStatus: mapDefaultPairToSelect(detail.maritalStatusOptions),
	};

	return { values, fieldMeta, beneficialOwnersData, beneficialOwnerOptions };
};

// ═══════════════════════════════════════════════════════════════════════════
// TAB 3: ASSET DETAIL → Form Values
// ═══════════════════════════════════════════════════════════════════════════

export type AssetMappingResult = MappingResult & {
	assetMeta: QcPdAssetDetailMeta | null;
	customerAddress: {
		alamat: string;
		rt: string;
		rw: string;
		kodePos: string;
		kelurahan: string;
		kecamatan: string;
		kota: string;
	};
};

export const mapAssetDetailToForm = (
	detail: QcPdAssetDetailResponse,
): AssetMappingResult => {
	const values: Record<string, unknown> = {};
	const fieldMeta: FieldMetaMap = {};
	const { setStr, setNum, setBool, setSelect, setSelectStr } = createSetters(
		values,
		fieldMeta,
	);

	// Dealer
	const ds = detail.dealerSection;
	if (ds) {
		setStr('assetDealer', ds.dealer);
	}

	// Asset Detail
	const ad = detail.assetDetail;
	if (ad) {
		setStr('assetDescription', ad.assetDescription);
		setNum('assetTahunPerakitan', ad.assemblyYear);
		setNum('assetManufacturingYear', ad.manufacturingYear);
		// PHMJB: BE may return 0 to mean "not appraised yet" — show empty in the input.
		// Meta still extracted so editable/required are honored.
		if (ad.phJmbAmount) {
			values['assetPhmjb'] =
				ad.phJmbAmount.value && ad.phJmbAmount.value > 0
					? ad.phJmbAmount.value
					: undefined;
			fieldMeta['assetPhmjb'] = extractMeta(ad.phJmbAmount);
		}
		setNum('assetHargaOtr', ad.otrAmount);
		setNum('assetOtrPrice', ad.otrAmount);
		setNum('assetUangMuka', ad.downPaymentAmount);
		setNum('assetDownPayment', ad.downPaymentAmount);
		setStr('assetKondisi', ad.assetCondition);
		setStr('assetUsedNew', ad.assetCondition);
		setStr('assetUsage', ad.assetUsage);
		if (ad.category) {
			setSelect(
				'assetKategori',
				ad.category as unknown as QcFieldMetaWithOptions<number | null>,
			);
			// Blank the form value if the API value (e.g. 0) has no matching option in the dropdown.
			const meta = fieldMeta['assetKategori'];
			const current = values['assetKategori'];
			if (
				current != null &&
				meta?.options &&
				!meta.options.some((o) => o.value === current)
			) {
				values['assetKategori'] = undefined;
			}
		}
		setStr('assetPengguna', ad.user);
		setStr('assetPenggunaan', ad.usageDescription);
		setStr('assetLokasiPenggunaan', ad.usageLocation);
		setStr('assetLokasiPenyimpanan', ad.storageLocation);
	}

	// Karoseri Selection
	const ks = detail.karoseriSelection;
	if (ks) {
		setStr('assetSupplierKaroseri', ks.supplierKaroseri);
		setStr('assetKaroseriItem', ks.karoseriItem);
		setNum('assetItemPricePerUnit', ks.itemPricePerUnitAmount);
		setNum('assetItemQty', ks.itemQty);
		setNum('assetTotalDownPayment', ks.totalDownPaymentAmount);
		setNum('assetTotalPriceKaroseriItem', ks.totalPriceKaroseriItemAmount);
		setNum('assetTotalDpAllKaroseri', ks.totalDownPaymentAllKaroseriItemAmount);
	}

	// Asset Registration
	const ar = detail.assetRegistration;
	if (ar) {
		setStr('assetNamaPemilikBpkb', ar.bpkbOwnerName);
		setBool('useCustomerAddress', ar.useCustomerAddress);
		setStr('assetBpkbAlamat', ar.fullAddress);
		setStr('assetBpkbRt', ar.rt);
		setStr('assetBpkbKodePos', ar.postalCode);
		setStr('assetBpkbKelurahan', ar.subDistrict);
		setStr('assetBpkbKecamatan', ar.district);
		setStr('assetBpkbKota', ar.city);

		// New BPKB Owner
		const nb = ar.newBpkbOwner;
		if (nb) {
			setSelect('assetBpkbBaruAtasNama', nb.newBpkbOwnerOnBehalfOf);
			setStr('assetBpkbBaruNama', nb.newBpkbOwnerName);
			setSelect('assetBpkbBaruCustomerType', nb.customerType);
			setStr('assetBpkbBaruNoIdentitas', nb.identityNumber);
			setStr('assetBpkbBaruAlamat', nb.fullAddress);
			setStr('assetBpkbBaruRt', nb.rt);
			setStr('assetBpkbBaruKodePos', nb.postalCode);
			setStr('assetBpkbBaruKelurahan', nb.subDistrict);
			setStr('assetBpkbBaruKecamatan', nb.district);
			setStr('assetBpkbBaruKota', nb.city);
			setSelect('assetBpkbBaruJenisBadanUsaha', nb.businessEntityType);
			setStr('assetBpkbBaruNoSiupp', nb.siuppNumber);
			setStr('assetBpkbBaruNoTdp', nb.tdpNumber);
			setStr('assetBpkbBaruNoAkta', nb.deedNumber);
			setStr('assetBpkbBaruNoNpwp', nb.npwpNumber);
		}
	}

	// Asset Insurance
	const ai = detail.assetInsurance;
	if (ai) {
		values['assetDiasuransikanOleh'] = ai.insuredBy?.value ?? '';
		const insuredByOpts = (ai.insuredBy?.options ?? []).map((o) => ({
			label: o.value ?? '',
			value: o.value ?? '',
		}));
		fieldMeta['assetDiasuransikanOleh'] = {
			editable: ai.insuredBy?.editable ?? true,
			required: ai.insuredBy?.required ?? false,
			...(insuredByOpts.length ? { options: insuredByOpts } : {}),
		};

		values['assetAsuransiDibayarOleh'] = ai.insurancePaidBy?.value ?? '';
		const paidByOpts = (ai.insurancePaidBy?.options ?? []).map((o) => ({
			label: o.value ?? '',
			value: o.value ?? '',
		}));
		fieldMeta['assetAsuransiDibayarOleh'] = {
			editable: ai.insurancePaidBy?.editable ?? true,
			required: ai.insurancePaidBy?.required ?? false,
			...(paidByOpts.length ? { options: paidByOpts } : {}),
		};
	}

	// Showroom Employee
	const se = detail.showroomEmployee;
	if (se) {
		setStr('assetSalesman', se.salesman);
		setStr('assetSupplierAdmin', se.supplierAdmin);
		setStr('assetSalesSupervisor', se.salesSupervisor);
		setStr('assetSalesCoordinator', se.salesCoordinator);
	}

	// TES Employee — QcFieldMetaWithOptions<string | null>: option value = display string
	const te = detail.tesEmployee;
	if (te) {
		setSelectStr('assetAccountOfficer', te.accountOfficer);
		setSelectStr('assetSurveyor', te.surveyor);
		setSelectStr('assetCreditAnalyst', te.creditAnalyst);
	}

	// Supplier Employee — QcFieldMetaWithOptions<string | null>: option value = display string
	const spe = detail.supplierEmployee;
	if (spe) {
		setSelectStr('assetSalesmanSp', spe.salesman);
		setSelectStr('assetSupplierAdminSp', spe.supplierAdmin);
		setSelectStr('assetSalesSupervisorSp', spe.salesSupervisor);
		setSelectStr('assetSalesCoordinatorSp', spe.salesCoordinator);
	}

	// Extract customer address from registration for "use customer address" checkbox
	const customerAddress = {
		alamat: ar?.fullAddress?.value ?? '',
		rt: ar?.rt?.value ?? '',
		rw: ar?.rw?.value ?? '',
		kodePos: ar?.postalCode?.value ?? '',
		kelurahan: ar?.subDistrict?.value ?? '',
		kecamatan: ar?.district?.value ?? '',
		kota: ar?.city?.value ?? '',
	};

	return { values, fieldMeta, assetMeta: detail.meta ?? null, customerAddress };
};

// ═══════════════════════════════════════════════════════════════════════════
// TAB 4: INSURANCE DETAIL → Form Values
// ═══════════════════════════════════════════════════════════════════════════

export type InsuranceMappingResult = MappingResult & {
	paymentSchedule: QcPdInsurancePaymentScheduleItem[];
	tenorYears: number[];
	paymentDocumentUrl: string;
	policyDocumentUrl: string;
};

export const mapInsuranceDetailToForm = (
	detail: QcPdInsuranceDetailResponse,
): InsuranceMappingResult => {
	const values: Record<string, unknown> = {};
	const fieldMeta: FieldMetaMap = {};
	const { setStr, setNum, setBool } = createSetters(values, fieldMeta);

	// Insurance Data
	const id_ = detail.insuranceData;
	if (id_) {
		setStr('insWilayahAsuransi', id_.insuranceRegion);
		setStr('insDiasuransikanOleh', id_.insuredBy);
		setStr('insDibayarkanOleh', id_.paidBy);
		setNum('insTenorCredit', id_.tenor);
		setStr('insTipeAplikasi', id_.applicationType);
		setNum('insNilaiPertanggungan', id_.coverageAmount);
		setStr('insRateJual', id_.sellingRate);
		setStr('insRateJualAcp', id_.sellingRateAcp);
	}

	// Insurance Entry
	const ie = detail.insuranceEntry;
	if (ie) {
		setStr('insTipeAsuransi', ie.insuranceType);
		setStr('insPembayaranAsuransi', ie.insurancePayment);
		setStr('insAsuransiTenor', ie.insuranceTenor);
		setStr('insPilihPembayaran', ie.paymentMethod);
		setStr('insSkemaPembayaran', ie.paymentScheme);
		setNum('insTjh', ie.tjhAmount);
		setBool('insHuruHara', ie.isRiot);
		setBool('insBanjir', ie.isFlood);
		setBool('insGempa', ie.isEarthquake);
		setBool('insTeroris', ie.isTerrorism);
		setNum('insPaPengemudi', ie.driverPersonalAccident);
		setNum('insJumlahPenumpang', ie.totalPassenger);
		setBool('insAsuransiGap', ie.isGap);
		setBool('insAsuransiHospitalInCar', ie.isHic);
		setNum('insDiscountAsuransi', ie.insuranceDiscountPercentage);
		setNum(
			'insSubsidiAsuransiPihakKetiga',
			ie.thirdPartyInsuranceSubsidyAmount,
		);
		setNum('insBiayaAdminPokokHutang', ie.adminCostIncludedInPrincipalAmount);
		setNum('insBiayaFiduciaPokokHutang', ie.fiduciaryCostPaidByPrincipalAmount);
		setNum('insBiayaProvisiPokokHutang', ie.provisionCostPaidByPrincipalAmount);
	}

	return {
		values,
		fieldMeta,
		paymentSchedule: detail.insuranceEntry?.paymentSchedule ?? [],
		tenorYears: detail.insuranceEntry?.tenorYears ?? [],
		paymentDocumentUrl: detail.insuranceData?.paymentDocument?.name ?? '',
		policyDocumentUrl: detail.insuranceData?.policyDocument?.name ?? '',
	};
};

// ═══════════════════════════════════════════════════════════════════════════
// TAB 5: FINANCIAL DETAIL → Form Values
// ═══════════════════════════════════════════════════════════════════════════

export const mapFinancialDetailToForm = (
	detail: QcPdFinancialDetailResponse,
): MappingResult => {
	const values: Record<string, unknown> = {};
	const fieldMeta: FieldMetaMap = {};
	const { setStr, setNum } = createSetters(values, fieldMeta);

	const f = detail.financial;
	if (f) {
		setNum('hargaKendaraan', f.vehiclePriceAmount);
		setNum('pokokHutang', f.principalDebtAmount);
		setNum('premiAsuransi', f.insurancePremiumAmount);
		setNum('pembayaranAsuransiDiterimaDimuka', f.insurancePrepaidPaymentAmount);
		setNum('asuransiMenambahPokokHutang', f.insuranceAddedToPrincipalAmount);
		setNum('bungaEfektif', f.effectiveInterestPercentage);
		setNum('subsidiBungaDariDealer', f.dealerInterestSubsidyAmount);
		setNum('subsidiBungaDariAtpm', f.atpmInterestSubsidyAmount);
		setNum('komisiKeDealer', f.dealerCommissionAmount);
		setNum('subsidiRiskDariDealer', f.dealerRiskSubsidyAmount);
		setNum('jumlahPeriodeAngsuran', f.installmentPeriodCount);
		setNum('nilaiAngsuran', f.installmentValueAmount);
		setNum('uangMuka', f.downPaymentAmount);
		setNum('sukuBungaJual', f.sellingInterestRatePercentage);
		setNum('subsidiDariAhmMotor', f.ahmMotorSubsidyAmount);
		setNum('subsidiDariMtf', f.mtfSubsidyAmount);
		setStr('angsuranPertamaFinancial', f.firstInstallmentType);
	}

	return { values, fieldMeta };
};

// ═══════════════════════════════════════════════════════════════════════════
// TAB 6: INCENTIVE DETAIL → Form Values
// ═══════════════════════════════════════════════════════════════════════════

export type IncentiveMappingResult = MappingResult & {
	distributionData: Array<{
		key: number;
		penerima: string;
		namaKaryawan: string;
		noRekening: string;
		namaRekening: string;
		sisaPremi: number;
		kelebihanAdmin: number;
		kelebihanBunga: number;
		kelebihanProvisi: number;
	}>;
	showroomEmployeeOptions: Record<string, { label: string; value: string }[]>;
	mandiriEmployeeOptions: Record<string, { label: string; value: string }[]>;
	documentUploadUrl: string;
};

export const mapIncentiveDetailToForm = (
	detail: QcPdIncentiveDetailResponse,
): IncentiveMappingResult => {
	const values: Record<string, unknown> = {};
	const fieldMeta: FieldMetaMap = {};
	const { setStr, setNum, setSelect } = createSetters(values, fieldMeta);

	// Top fields
	setStr('incNamaDealer', detail.dealerName);
	setStr('incNamaCustomer', detail.customerName);

	// Insurance Data summary
	const ins = detail.insuranceData;
	if (ins) {
		setNum('incPengembalianPremiPersen', ins.premiumRefundPercentageForDealer);
		setNum('incPremiDasar', ins.basePremiumAmountForDealer);
		setNum(
			'incPengembalianPremiNominal',
			ins.residualPremiumRefundAmountForDealer,
		);
	}

	// Cost Data summary
	const cost = detail.costData;
	if (cost) {
		setNum('incMaksimalAdmin', cost.maxAdminExcessRefundAmount);
		setNum('incMaksimalProvisi', cost.maxProvisionExcessRefundAmount);
	}

	// Financial Data summary
	const fin = detail.financialData;
	if (fin) {
		setNum('incMaksimalBunga', fin.maxInterestExcessRefundAmount);
		setNum('incRefundSubsidiBunga', fin.interestSubsidyRefundAmountForDealer);
	}

	// Showroom Employees
	const se = detail.showroomEmployees;
	const showroomEmployeeOptions: Record<
		string,
		{ label: string; value: string }[]
	> = {};
	if (se) {
		setSelect('incSalesman', se.salesman);
		setSelect('incSupplierAdmin', se.supplierAdmin);
		setSelect('incBranchManager', se.branchManager);
		setSelect('incSalesSupervisor', se.salesSupervisor);
		setSelect('incSalesCoordinator', se.salesCoordinator);
		showroomEmployeeOptions['incSalesman'] = mapOptionsToSelect(se.salesman);
		showroomEmployeeOptions['incSupplierAdmin'] = mapOptionsToSelect(
			se.supplierAdmin,
		);
		showroomEmployeeOptions['incBranchManager'] = mapOptionsToSelect(
			se.branchManager,
		);
		showroomEmployeeOptions['incSalesSupervisor'] = mapOptionsToSelect(
			se.salesSupervisor,
		);
		showroomEmployeeOptions['incSalesCoordinator'] = mapOptionsToSelect(
			se.salesCoordinator,
		);
	}

	// Mandiri Employees
	const me = detail.mandiriEmployees;
	const mandiriEmployeeOptions: Record<
		string,
		{ label: string; value: string }[]
	> = {};
	if (me) {
		setSelect('incMandiriEmployee', me.mandiriEmployee);
		setSelect('incBmMandiri', me.mandiriBranchManager);
		setSelect('incMandiriKpmNo', me.mandiriKpmNo);
		mandiriEmployeeOptions['incMandiriEmployee'] = mapOptionsToSelect(
			me.mandiriEmployee,
		);
		mandiriEmployeeOptions['incBmMandiri'] = mapOptionsToSelect(
			me.mandiriBranchManager,
		);
		mandiriEmployeeOptions['incMandiriKpmNo'] = mapOptionsToSelect(
			me.mandiriKpmNo,
		);
	}

	// Distribution table
	const distItems = detail.incentiveDistribution?.items ?? [];
	const distributionData = distItems.map(
		(item: QcIncentiveDistributionItemResponse, index: number) => ({
			key: index,
			penerima: item.recipientType ?? '',
			namaKaryawan: item.employeeName ?? '',
			noRekening: item.bankAccountNo ?? '',
			namaRekening: item.bankAccountName ?? '',
			sisaPremi: item.residualPremiumRefundAmount?.value ?? 0,
			kelebihanAdmin: item.adminExcessRefundAmount?.value ?? 0,
			kelebihanBunga: item.interestExcessRefundAmount?.value ?? 0,
			kelebihanProvisi: item.provisionExcessRefundAmount?.value ?? 0,
		}),
	);

	return {
		values,
		fieldMeta,
		distributionData,
		showroomEmployeeOptions,
		mandiriEmployeeOptions,
		documentUploadUrl: detail.documentUpload?.name ?? '',
	};
};

// ═══════════════════════════════════════════════════════════════════════════
// DEBUG: local form name → API response path
// Only used by DebugFieldTag (?debug=true). Not referenced in production code.
// ═══════════════════════════════════════════════════════════════════════════

export const FIELD_API_PATHS: Record<string, string> = {
	// ── Customer Data (top fields) ──────────────────────────────────────────
	namaSesuaiAkta: 'customerData.legalName',
	npwp: 'customerData.npwpNo',
	namaDataBank: 'customerData.nameForBank',
	alamatLengkap: 'customerData.address',
	rt: 'customerData.rt',
	rw: 'customerData.rw',
	kodePos: 'customerData.postalCode',
	kelurahan: 'customerData.subDistrict',
	kecamatan: 'customerData.district',
	kota: 'customerData.city',
	noTelepon: 'customerData.phoneNo',
	// ── Company Data ─────────────────────────────────────────────────────────
	tempatAktaDiterbitkan: 'companyData.deedIssuedPlace',
	tahunPendirian: 'companyData.deedFoundingYear',
	jenisPerusahaan: 'companyData.companyType',
	emailPerusahaan: 'companyData.companyEmail',
	jumlahKaryawan: 'companyData.numberOfEmployees',
	groupBmpp: 'companyData.groupBmppName',
	targetMarket: 'companyData.targetMarket',
	goPublic: 'companyData.isGoPublic',
	statusPerusahaan: 'companyData.companyStatus',
	// ── Legal Address ────────────────────────────────────────────────────────
	alamatLengkapAkta: 'legalAddress.address',
	rtAkta: 'legalAddress.rt',
	rwAkta: 'legalAddress.rw',
	addressAkta: 'legalAddress.postalCode',
	kelurahanAkta: 'legalAddress.subDistrict',
	kecamatanAkta: 'legalAddress.district',
	kotaAkta: 'legalAddress.city',
	noTeleponAkta: 'legalAddress.phoneNo',
	noHandphoneAkta: 'legalAddress.mobileNo',
	// ── Operational Address ──────────────────────────────────────────────────
	useAlamatLegal: 'operationalAddress.useSameAsLegalAddress',
	alamatLengkapOperasional: 'operationalAddress.address',
	rtOperasional: 'operationalAddress.rt',
	rwOperasional: 'operationalAddress.rw',
	addressOperasional: 'operationalAddress.postalCode',
	kelurahanOperasional: 'operationalAddress.subDistrict',
	kecamatanOperasional: 'operationalAddress.district',
	kotaOperasional: 'operationalAddress.city',
	noTeleponOperasional: 'operationalAddress.phoneNo',
	// ── Financial Data (customer tab) ────────────────────────────────────────
	penghasilanPerBulan: 'financialData.monthlyIncomeAmount',
	rasioLancar: 'financialData.currentRatioPercentage',
	tingkatRoi: 'financialData.returnOnInvestment',
	rasioAngsuranPenghasilan: 'financialData.installmentToIncomeRatioPercentage',
	jenisRekeningBank: 'financialData.bankAccountType',
	rataRataSaldoAkhir: 'financialData.averageEndingBalanceAmount',
	rataRataSaldoMengendap: 'financialData.averageDepositBalanceAmount',
	kasSetaraKas: 'financialData.cashAndCashEquivalentAmount',
	piutangUsahaLancar: 'financialData.tradeReceivableCurrentAssetAmount',
	investasiKeuanganLancar: 'financialData.investmentOtherFinancialCurrentAssetAmount',
	asetLancarLainnya: 'financialData.otherCurrentAssetAmount',
	totalAsetLancar: 'financialData.totalCurrentAssetAmount',
	piutangUsahaTidakLancar: 'financialData.tradeReceivableNonCurrentAssetAmount',
	investasiKeuanganTidakLancar: 'financialData.investmentOtherFinancialNonCurrentAssetAmount',
	asetTidakLancarLainnya: 'financialData.otherNonCurrentAssetAmount',
	totalAsetTidakLancar: 'financialData.totalNonCurrentAssetAmount',
	totalAset: 'financialData.totalAssetAmount',
	statusTempatUsaha: 'financialData.businessPlaceOwnershipStatus',
	menempatiTempatUsahaSejak: 'financialData.occupiedSinceYear',
	tanggalBerakhirnyaSewa: 'financialData.leaseEndDate',
	tahunLaporan: 'financialData.reportYear',
	aktivaLancar: 'financialData.currentAssetNonFixedAssetAmount',
	totalHutang: 'financialData.totalDebtAmount',
	hutangJangkaPendek: 'financialData.shortTermDebtAmount',
	modalDisetor: 'financialData.paidInCapitalAmount',
	nilaiPenjualan: 'financialData.salesValueAmount',
	pendapatanOperasional: 'financialData.operationalIncomeAmount',
	labaRugiBruto: 'financialData.grossProfitLossAmount',
	pendapatanNonOperasional: 'financialData.nonOperationalIncomeAmount',
	labaRugiSebelumPajak: 'financialData.profitLossBeforeTaxAmount',
	labaRugiTahunBerjalan: 'financialData.currentYearProfitLossAmount',
	statusAudit: 'financialData.isAudited',
	diAuditOleh: 'financialData.auditedBy',
	pinjamanJangkaPendek: 'financialData.shortTermLoanLiabilityAmount',
	utangUsahaJangkaPendek: 'financialData.tradePayableShortTermLiabilityAmount',
	liabilitasJangkaPendekLainnya: 'financialData.otherShortTermLiabilityAmount',
	liabilitasJangkaPendek: 'financialData.totalShortTermLiabilityAmount',
	pinjamanJangkaPanjang: 'financialData.longTermLoanLiabilityAmount',
	utangUsahaJangkaPanjang: 'financialData.tradePayableLongTermLiabilityAmount',
	liabilitasJangkaPanjangLainnya: 'financialData.otherLongTermLiabilityAmount',
	liabilitasJangkaPanjang: 'financialData.totalLongTermLiabilityAmount',
	liabilitas: 'financialData.totalLiabilityAmount',
	ekuitas: 'financialData.equityAmount',
	totalPassiva: 'financialData.totalPassiveAmount',
	// ── Warehouse / Gudang ──────────────────────────────────────────────────
	statusKepemilikanGudang: 'warehouseOfficeWorkshop.ownershipStatus',
	alamatGudang: 'warehouseOfficeWorkshop.address',
	rtGudang: 'warehouseOfficeWorkshop.rt',
	rwGudang: 'warehouseOfficeWorkshop.rw',
	kodeposGudang: 'warehouseOfficeWorkshop.postalCode',
	kelurahanGudang: 'warehouseOfficeWorkshop.subDistrict',
	kecamatanGudang: 'warehouseOfficeWorkshop.district',
	kotaGudang: 'warehouseOfficeWorkshop.city',
	noTeleponGudang: 'warehouseOfficeWorkshop.phoneNo',
	// ── Bank Account ─────────────────────────────────────────────────────────
	namaBank: 'bankAccount.bankName',
	namaCabangBank: 'bankAccount.branchName',
	nomorRekening: 'bankAccount.accountNumber',
	// ── Other Data ───────────────────────────────────────────────────────────
	setujuPemasaranPihakKetiga: 'otherData.isAgreeThirdPartyMarketingPersonalComm',
	pernahKreditMobil: 'otherData.hasAppliedForVehicleCredit',
	daftarHitamAppi: 'otherData.isOnAppiBlacklist',
	setujuPemrosesan: 'otherData.isAgreeDataProcessingForFinancing',
	setujuPemasaranMtf: 'otherData.isAgreeMtfMarketingPersonalComm',
	// ── PIC Tanda Tangan ─────────────────────────────────────────────────────
	namaPicTtd: 'picDocumentSigner.name',
	alamatLengkapPicTtd: 'picDocumentSigner.address',
	noKtpPicTtd: 'picDocumentSigner.idNumber',
	jabatanPicTtd: 'picDocumentSigner.position',
	// ── PIC Penagihan ────────────────────────────────────────────────────────
	namaPicPenagihan: 'picBilling.name',
	jabatanPicPenagihan: 'picBilling.position',
	noTeleponPicPenagihan: 'picBilling.phoneNumber',
	noTelepon2PicPenagihan: 'picBilling.phoneNumber2',
	// ── Application: Product Information ────────────────────────────────────
	productCustomerType: 'productInformation.productCustomerType',
	mandiriKpm: 'productInformation.mandiriKpm',
	jenisAssetApp: 'productInformation.assetType',
	angsuranPertama: 'productInformation.firstInstallment',
	spesifikProduct: 'productInformation.specificProduct',
	produk: 'productInformation.product',
	kondisiKendaraan: 'productInformation.vehicleCondition',
	tenorApp: 'productInformation.tenor',
	productOffering: 'productInformation.productOffering',
	tahunPerakitan: 'productInformation.assemblyYear',
	mklNo: 'productInformation.mklNo',
	poNumber: 'productInformation.poNumber',
	installmentScheme: 'productInformation.installmentScheme',
	interestRate: 'productInformation.interestRate',
	multipleAgreement: 'productInformation.multipleAgreement',
	copyAgreement: 'productInformation.copyAgreement',
	// ── Application: Entry Application Information ───────────────────────────
	jenisPembayaran: 'entryApplicationInformation.paymentType',
	tanggalSurvey: 'entryApplicationInformation.surveyDate',
	sumberAplikasi: 'entryApplicationInformation.applicationSource',
	// ── Application: Bank Account ────────────────────────────────────────────
	namaBankApp: 'bankAccount.bankName',
	bankBranch: 'bankAccount.bankBranch',
	accountNo: 'bankAccount.accountNumber',
	// ── Application: MTF Employee ────────────────────────────────────────────
	accountOfficer: 'mtfEmployee.accountOfficer',
	creditAnalyst: 'mtfEmployee.creditAnalyst',
	surveyor: 'mtfEmployee.surveyor',
	fiducia: 'mtfEmployee.fiduciaryAmount',
	admin: 'mtfEmployee.adminAmount',
	provisi: 'mtfEmployee.provisionAmount',
	serviceFee: 'mtfEmployee.serviceFeeAmount',
	refundAdministration: 'mtfEmployee.refundAdministrationAmount',
	refundProvisi: 'mtfEmployee.refundProvisionAmount',
	// ── Application: Non-Resident Reference ─────────────────────────────────
	namaReferensi: 'nonResidentReference.name',
	hubunganReferensi: 'nonResidentReference.relationshipWithCustomer',
	alamatLengkapReferensi: 'nonResidentReference.fullAddress',
	rtReferensi: 'nonResidentReference.rt',
	rwReferensi: 'nonResidentReference.rw',
	kelurahanReferensi: 'nonResidentReference.kelurahan',
	kodePosReferensi: 'nonResidentReference.postalCode',
	kecamatanReferensi: 'nonResidentReference.kecamatan',
	noTeleponReferensi: 'nonResidentReference.phoneNumber',
	kotaReferensi: 'nonResidentReference.city',
	// ── Application: Environment Survey ─────────────────────────────────────
	namaSumberInformasi: 'environmentSurvey.informationSourceName',
	hubunganSurvey: 'environmentSurvey.relationshipWithCustomer',
	noHandphoneSurvey: 'environmentSurvey.mobilePhone',
	karakterCustomer: 'environmentSurvey.customerCharacter',
	// ── Application: Mailing Address ─────────────────────────────────────────
	salinAlamatDari: 'mailingAddress.copyAddressFrom',
	alamatLengkapMailing: 'mailingAddress.fullAddress',
	rtMailing: 'mailingAddress.rt',
	rwMailing: 'mailingAddress.rw',
	kelurahanMailing: 'mailingAddress.kelurahan',
	kodePosMailing: 'mailingAddress.postalCode',
	kecamatanMailing: 'mailingAddress.kecamatan',
	noTeleponMailing: 'mailingAddress.phoneNumber',
	kotaMailing: 'mailingAddress.city',
	jarakDariKantorMtf: 'mailingAddress.distanceFromMtfOfficeKm',
	// ── Asset: Dealer ────────────────────────────────────────────────────────
	assetDealer: 'dealerSection.dealer',
	// ── Asset: Asset Detail ──────────────────────────────────────────────────
	assetDescription: 'assetDetail.assetDescription',
	assetTahunPerakitan: 'assetDetail.assemblyYear',
	assetManufacturingYear: 'assetDetail.manufacturingYear',
	assetPhmjb: 'assetDetail.phJmbAmount',
	assetHargaOtr: 'assetDetail.otrAmount',
	assetOtrPrice: 'assetDetail.otrAmount',
	assetUangMuka: 'assetDetail.downPaymentAmount',
	assetDownPayment: 'assetDetail.downPaymentAmount',
	assetKondisi: 'assetDetail.assetCondition',
	assetUsedNew: 'assetDetail.assetCondition',
	assetUsage: 'assetDetail.assetUsage',
	assetKategori: 'assetDetail.category',
	assetPengguna: 'assetDetail.user',
	assetPenggunaan: 'assetDetail.usageDescription',
	assetLokasiPenggunaan: 'assetDetail.usageLocation',
	assetLokasiPenyimpanan: 'assetDetail.storageLocation',
	// ── Asset: Karoseri Selection ────────────────────────────────────────────
	assetSupplierKaroseri: 'karoseriSelection.supplierKaroseri',
	assetKaroseriItem: 'karoseriSelection.karoseriItem',
	assetItemPricePerUnit: 'karoseriSelection.itemPricePerUnitAmount',
	assetItemQty: 'karoseriSelection.itemQty',
	assetTotalDownPayment: 'karoseriSelection.totalDownPaymentAmount',
	assetTotalPriceKaroseriItem: 'karoseriSelection.totalPriceKaroseriItemAmount',
	assetTotalDpAllKaroseri: 'karoseriSelection.totalDownPaymentAllKaroseriItemAmount',
	// ── Asset: Registration ──────────────────────────────────────────────────
	assetNamaPemilikBpkb: 'assetRegistration.bpkbOwnerName',
	useCustomerAddress: 'assetRegistration.useCustomerAddress',
	assetBpkbAlamat: 'assetRegistration.fullAddress',
	assetBpkbRt: 'assetRegistration.rt',
	assetBpkbKodePos: 'assetRegistration.postalCode',
	assetBpkbKelurahan: 'assetRegistration.subDistrict',
	assetBpkbKecamatan: 'assetRegistration.district',
	assetBpkbKota: 'assetRegistration.city',
	assetBpkbBaruAtasNama: 'assetRegistration.newBpkbOwner.newBpkbOwnerOnBehalfOf',
	assetBpkbBaruNama: 'assetRegistration.newBpkbOwner.newBpkbOwnerName',
	assetBpkbBaruCustomerType: 'assetRegistration.newBpkbOwner.customerType',
	assetBpkbBaruNoIdentitas: 'assetRegistration.newBpkbOwner.identityNumber',
	assetBpkbBaruAlamat: 'assetRegistration.newBpkbOwner.fullAddress',
	assetBpkbBaruRt: 'assetRegistration.newBpkbOwner.rt',
	assetBpkbBaruKodePos: 'assetRegistration.newBpkbOwner.postalCode',
	assetBpkbBaruKelurahan: 'assetRegistration.newBpkbOwner.subDistrict',
	assetBpkbBaruKecamatan: 'assetRegistration.newBpkbOwner.district',
	assetBpkbBaruKota: 'assetRegistration.newBpkbOwner.city',
	assetBpkbBaruJenisBadanUsaha: 'assetRegistration.newBpkbOwner.businessEntityType',
	assetBpkbBaruNoSiupp: 'assetRegistration.newBpkbOwner.siuppNumber',
	assetBpkbBaruNoTdp: 'assetRegistration.newBpkbOwner.tdpNumber',
	assetBpkbBaruNoAkta: 'assetRegistration.newBpkbOwner.deedNumber',
	assetBpkbBaruNoNpwp: 'assetRegistration.newBpkbOwner.npwpNumber',
	// ── Asset: Insurance ────────────────────────────────────────────────────
	assetDiasuransikanOleh: 'assetInsurance.insuredBy',
	assetAsuransiDibayarOleh: 'assetInsurance.insurancePaidBy',
	// ── Asset: Showroom Employee ─────────────────────────────────────────────
	assetSalesman: 'showroomEmployee.salesman',
	assetSupplierAdmin: 'showroomEmployee.supplierAdmin',
	assetSalesSupervisor: 'showroomEmployee.salesSupervisor',
	assetSalesCoordinator: 'showroomEmployee.salesCoordinator',
	// ── Asset: TES Employee ──────────────────────────────────────────────────
	assetAccountOfficer: 'tesEmployee.accountOfficer',
	assetSurveyor: 'tesEmployee.surveyor',
	assetCreditAnalyst: 'tesEmployee.creditAnalyst',
	// ── Asset: Supplier Employee ─────────────────────────────────────────────
	assetSalesmanSp: 'supplierEmployee.salesman',
	assetSupplierAdminSp: 'supplierEmployee.supplierAdmin',
	assetSalesSupervisorSp: 'supplierEmployee.salesSupervisor',
	assetSalesCoordinatorSp: 'supplierEmployee.salesCoordinator',
	// ── Insurance Data ───────────────────────────────────────────────────────
	insWilayahAsuransi: 'insuranceData.insuranceRegion',
	insDiasuransikanOleh: 'insuranceData.insuredBy',
	insDibayarkanOleh: 'insuranceData.paidBy',
	insTenorCredit: 'insuranceData.tenor',
	insTipeAplikasi: 'insuranceData.applicationType',
	insNilaiPertanggungan: 'insuranceData.coverageAmount',
	insRateJual: 'insuranceData.sellingRate',
	insRateJualAcp: 'insuranceData.sellingRateAcp',
	// ── Insurance Entry ──────────────────────────────────────────────────────
	insTipeAsuransi: 'insuranceEntry.insuranceType',
	insPembayaranAsuransi: 'insuranceEntry.insurancePayment',
	insAsuransiTenor: 'insuranceEntry.insuranceTenor',
	insPilihPembayaran: 'insuranceEntry.paymentMethod',
	insSkemaPembayaran: 'insuranceEntry.paymentScheme',
	insTjh: 'insuranceEntry.tjhAmount',
	insHuruHara: 'insuranceEntry.isRiot',
	insBanjir: 'insuranceEntry.isFlood',
	insGempa: 'insuranceEntry.isEarthquake',
	insTeroris: 'insuranceEntry.isTerrorism',
	insPaPengemudi: 'insuranceEntry.driverPersonalAccident',
	insJumlahPenumpang: 'insuranceEntry.totalPassenger',
	insAsuransiGap: 'insuranceEntry.isGap',
	insAsuransiHospitalInCar: 'insuranceEntry.isHic',
	insDiscountAsuransi: 'insuranceEntry.insuranceDiscountPercentage',
	insSubsidiAsuransiPihakKetiga: 'insuranceEntry.thirdPartyInsuranceSubsidyAmount',
	insBiayaAdminPokokHutang: 'insuranceEntry.adminCostIncludedInPrincipalAmount',
	insBiayaFiduciaPokokHutang: 'insuranceEntry.fiduciaryCostPaidByPrincipalAmount',
	insBiayaProvisiPokokHutang: 'insuranceEntry.provisionCostPaidByPrincipalAmount',
	// ── Financial (PD tab) ───────────────────────────────────────────────────
	hargaKendaraan: 'financial.vehiclePriceAmount',
	pokokHutang: 'financial.principalDebtAmount',
	premiAsuransi: 'financial.insurancePremiumAmount',
	pembayaranAsuransiDiterimaDimuka: 'financial.insurancePrepaidPaymentAmount',
	asuransiMenambahPokokHutang: 'financial.insuranceAddedToPrincipalAmount',
	bungaEfektif: 'financial.effectiveInterestPercentage',
	subsidiBungaDariDealer: 'financial.dealerInterestSubsidyAmount',
	subsidiBungaDariAtpm: 'financial.atpmInterestSubsidyAmount',
	komisiKeDealer: 'financial.dealerCommissionAmount',
	subsidiRiskDariDealer: 'financial.dealerRiskSubsidyAmount',
	jumlahPeriodeAngsuran: 'financial.installmentPeriodCount',
	nilaiAngsuran: 'financial.installmentValueAmount',
	uangMuka: 'financial.downPaymentAmount',
	sukuBungaJual: 'financial.sellingInterestRatePercentage',
	subsidiDariAhmMotor: 'financial.ahmMotorSubsidyAmount',
	subsidiDariMtf: 'financial.mtfSubsidyAmount',
	angsuranPertamaFinancial: 'financial.firstInstallmentType',
	// ── Incentive ────────────────────────────────────────────────────────────
	incNamaDealer: 'dealerName',
	incNamaCustomer: 'customerName',
	incPengembalianPremiPersen: 'insuranceData.premiumRefundPercentageForDealer',
	incPremiDasar: 'insuranceData.basePremiumAmountForDealer',
	incPengembalianPremiNominal: 'insuranceData.residualPremiumRefundAmountForDealer',
	incMaksimalAdmin: 'costData.maxAdminExcessRefundAmount',
	incMaksimalProvisi: 'costData.maxProvisionExcessRefundAmount',
	incMaksimalBunga: 'financialData.maxInterestExcessRefundAmount',
	incRefundSubsidiBunga: 'financialData.interestSubsidyRefundAmountForDealer',
	incSalesman: 'showroomEmployees.salesman',
	incSupplierAdmin: 'showroomEmployees.supplierAdmin',
	incBranchManager: 'showroomEmployees.branchManager',
	incSalesSupervisor: 'showroomEmployees.salesSupervisor',
	incSalesCoordinator: 'showroomEmployees.salesCoordinator',
	incMandiriEmployee: 'mandiriEmployees.mandiriEmployee',
	incBmMandiri: 'mandiriEmployees.mandiriBranchManager',
	incMandiriKpmNo: 'mandiriEmployees.mandiriKpmNo',
};

// ═══════════════════════════════════════════════════════════════════════════
// FORM → SAVE REQUEST MAPPERS
// ═══════════════════════════════════════════════════════════════════════════

export const mapFormToSaveCustomer = (
	purchaseOrderNo: string,
	customerId: string,
	values: Record<string, unknown>,
	action: QcCustomerAction,
): SaveQcCustomerRequest & { purchaseOrderNo: string | null } => {
	const v = values;
	return {
		purchaseOrderNo,
		customerId,
		action,
		customerData: {
			legalName: str(v, 'namaSesuaiAkta'),
			nameForBank: str(v, 'namaDataBank'),
			npwpNo: str(v, 'npwp'),
			address: str(v, 'alamatLengkap'),
			rt: str(v, 'rt'),
			rw: str(v, 'rw'),
			postalCode: str(v, 'kodePos'),
			phoneNo: str(v, 'noTelepon'),
		},
		companyData: {
			deedIssuedPlace: str(v, 'tempatAktaDiterbitkan'),
			deedFoundingYear: str(v, 'tahunPendirian'),
			companyType: str(v, 'jenisPerusahaan'),
			companyEmail: str(v, 'emailPerusahaan'),
			numberOfEmployees: num(v, 'jumlahKaryawan') as number | null,
			groupBmppName: str(v, 'groupBmpp'),
			targetMarket: int(v, 'targetMarket'),
			isGoPublic: bool(v, 'goPublic'),
			companyStatus: int(v, 'statusPerusahaan'),
		},
		legalAddress: {
			address: str(v, 'alamatLengkapAkta'),
			rt: str(v, 'rtAkta'),
			rw: str(v, 'rwAkta'),
			postalCode:
				(v['addressAkta'] as { postalCode?: string })?.postalCode || null,
			phoneNo: str(v, 'noTeleponAkta'),
			mobileNo: str(v, 'noHandphoneAkta'),
		},
		operationalAddress: {
			useSameAsLegalAddress: bool(v, 'useAlamatLegal'),
			address: str(v, 'alamatLengkapOperasional'),
			rt: str(v, 'rtOperasional'),
			rw: str(v, 'rwOperasional'),
			postalCode:
				(v['addressOperasional'] as { postalCode?: string })?.postalCode ||
				null,
			phoneNo: str(v, 'noTeleponOperasional'),
		},
		financialData: {
			monthlyIncomeAmount: num(v, 'penghasilanPerBulan'),
			currentRatioPercentage: num(v, 'rasioLancar'),
			returnOnInvestment: num(v, 'tingkatRoi'),
			installmentToIncomeRatioPercentage: num(v, 'rasioAngsuranPenghasilan'),
			bankAccountType: int(v, 'jenisRekeningBank'),
			averageEndingBalanceAmount: num(v, 'rataRataSaldoAkhir'),
			averageDepositBalanceAmount: num(v, 'rataRataSaldoMengendap'),
			cashAndCashEquivalentAmount: num(v, 'kasSetaraKas'),
			tradeReceivableCurrentAssetAmount: num(v, 'piutangUsahaLancar'),
			investmentOtherFinancialCurrentAssetAmount: num(
				v,
				'investasiKeuanganLancar',
			),
			otherCurrentAssetAmount: num(v, 'asetLancarLainnya'),
			totalCurrentAssetAmount: num(v, 'totalAsetLancar'),
			tradeReceivableNonCurrentAssetAmount: num(v, 'piutangUsahaTidakLancar'),
			investmentOtherFinancialNonCurrentAssetAmount: num(
				v,
				'investasiKeuanganTidakLancar',
			),
			otherNonCurrentAssetAmount: num(v, 'asetTidakLancarLainnya'),
			totalNonCurrentAssetAmount: num(v, 'totalAsetTidakLancar'),
			totalAssetAmount: num(v, 'totalAset'),
			businessPlaceOwnershipStatus: int(v, 'statusTempatUsaha'),
			occupiedSinceYear: str(v, 'menempatiTempatUsahaSejak'),
			leaseEndDate: dateStr(v, 'tanggalBerakhirnyaSewa'),
			reportYear: str(v, 'tahunLaporan'),
			currentAssetNonFixedAssetAmount: num(v, 'aktivaLancar'),
			totalDebtAmount: num(v, 'totalHutang'),
			shortTermDebtAmount: num(v, 'hutangJangkaPendek'),
			paidInCapitalAmount: num(v, 'modalDisetor'),
			salesValueAmount: num(v, 'nilaiPenjualan'),
			operationalIncomeAmount: num(v, 'pendapatanOperasional'),
			grossProfitLossAmount: num(v, 'labaRugiBruto'),
			nonOperationalIncomeAmount: num(v, 'pendapatanNonOperasional'),
			profitLossBeforeTaxAmount: num(v, 'labaRugiSebelumPajak'),
			currentYearProfitLossAmount: num(v, 'labaRugiTahunBerjalan'),
			isAudited: bool(v, 'statusAudit'),
			auditedBy: str(v, 'diAuditOleh'),
			shortTermLoanLiabilityAmount: num(v, 'pinjamanJangkaPendek'),
			tradePayableShortTermLiabilityAmount: num(v, 'utangUsahaJangkaPendek'),
			otherShortTermLiabilityAmount: num(v, 'liabilitasJangkaPendekLainnya'),
			totalShortTermLiabilityAmount: num(v, 'liabilitasJangkaPendek'),
			longTermLoanLiabilityAmount: num(v, 'pinjamanJangkaPanjang'),
			tradePayableLongTermLiabilityAmount: num(v, 'utangUsahaJangkaPanjang'),
			otherLongTermLiabilityAmount: num(v, 'liabilitasJangkaPanjangLainnya'),
			totalLongTermLiabilityAmount: num(v, 'liabilitasJangkaPanjang'),
			totalLiabilityAmount: num(v, 'liabilitas'),
			equityAmount: num(v, 'ekuitas'),
			totalPassiveAmount: num(v, 'totalPassiva'),
		},
		warehouseOfficeWorkshop: {
			ownershipStatus: int(v, 'statusKepemilikanGudang'),
			address: str(v, 'alamatGudang'),
			rt: str(v, 'rtGudang'),
			rw: str(v, 'rwGudang'),
			postalCode: str(v, 'kodeposGudang'),
			subDistrict: str(v, 'kelurahanGudang'),
			district: str(v, 'kecamatanGudang'),
			city: str(v, 'kotaGudang'),
			phoneNo: str(v, 'noTeleponGudang'),
		},
		bankAccount: {
			bankName: str(v, 'namaBank'),
			branchName: str(v, 'namaCabangBank'),
			accountNumber: str(v, 'nomorRekening'),
		},
		otherData: {
			isAgreeThirdPartyMarketingPersonalComm: bool(
				v,
				'setujuPemasaranPihakKetiga',
			),
			hasAppliedForVehicleCredit: bool(v, 'pernahKreditMobil'),
			isOnAppiBlacklist: bool(v, 'daftarHitamAppi'),
			isAgreeDataProcessingForFinancing: bool(v, 'setujuPemrosesan'),
			isAgreeMtfMarketingPersonalComm: bool(v, 'setujuPemasaranMtf'),
		},
		picDocumentSigner: {
			name: str(v, 'namaPicTtd'),
			address: str(v, 'alamatLengkapPicTtd'),
			idNumber: str(v, 'noKtpPicTtd'),
			position: str(v, 'jabatanPicTtd'),
		},
		picBilling: {
			name: str(v, 'namaPicPenagihan'),
			position: str(v, 'jabatanPicPenagihan'),
			phoneNumber: str(v, 'noTeleponPicPenagihan'),
			phoneNumber2: str(v, 'noTelepon2PicPenagihan'),
		},
		managementShareholders: (
			(v['sahamData'] as Record<string, unknown>[]) ?? []
		).map((sh) => ({
			name: (sh.nama as string) || null,
			citizenship:
				sh.kewarganegaraan != null
					? parseInt(String(sh.kewarganegaraan), 10)
					: null,
			nationality: (sh.kebangsaan as string) || null,
			identityCardType:
				sh.jenisIdentitas != null
					? parseInt(String(sh.jenisIdentitas), 10)
					: null,
			identityNumber: (sh.noIdentitas as string) || null,
			dateOfBirth:
				sh.tanggalLahir &&
				typeof sh.tanggalLahir === 'object' &&
				'format' in (sh.tanggalLahir as object)
					? (sh.tanggalLahir as dayjs.Dayjs).format('YYYY-MM-DD')
					: null,
			gender:
				sh.jenisKelamin != null ? parseInt(String(sh.jenisKelamin), 10) : null,
			phoneNumber: (sh.noTelepon as string) || null,
			position: sh.jabatan != null ? parseInt(String(sh.jabatan), 10) : null,
			address: (sh.alamat as string) || null,
			postalCode: (sh.kodePos as string) || null,
			kelurahan: (sh.kelurahan as string) || null,
			kecamatan: (sh.kecamatan as string) || null,
			city: (sh.kota as string) || null,
			rt: (sh.rt as string) || null,
			rw: (sh.rw as string) || null,
			status: sh.status != null ? parseInt(String(sh.status), 10) : null,
			isShareholder: (sh.isShareholder as boolean | null) ?? null,
			shareholding:
				sh.persentaseKepemilikan != null
					? Number(sh.persentaseKepemilikan)
					: null,
		})),
		legalDocuments: (
			(v['legalitasDokumen'] as Record<string, unknown>[]) ?? []
		).map((doc) => ({
			documentType: (doc.jenis as string) || null,
			documentNumber: (doc.noDokumen as string) || null,
			documentDate:
				doc.tanggalDokumen &&
				typeof doc.tanggalDokumen === 'object' &&
				'format' in (doc.tanggalDokumen as object)
					? (doc.tanggalDokumen as dayjs.Dayjs).format('YYYY-MM-DD')
					: null,
			notes: (doc.notes as string) || null,
		})),
	};
};

export const mapFormToSaveApplication = (
	purchaseOrderNo: string,
	values: Record<string, unknown>,
	action: QcApplicationAction,
): SaveQcPdApplicationRequest => {
	const v = values;
	return {
		purchaseOrderNo,
		action,
		productInformation: {
			specificProduct: int(v, 'spesifikProduct'),
			installmentScheme: int(v, 'installmentScheme'),
		},
		mtfEmployee: {
			refundAdministrationAmount: num(v, 'refundAdministration'),
			refundProvisiAmount: num(v, 'refundProvisi'),
		},
		nonResidentReference: {
			name: str(v, 'namaReferensi'),
			relationshipWithCustomer: str(v, 'hubunganReferensi'),
			fullAddress: str(v, 'alamatLengkapReferensi'),
			rt: str(v, 'rtReferensi'),
			rw: str(v, 'rwReferensi'),
			postalCode: str(v, 'kodePosReferensi'),
			kelurahan: str(v, 'kelurahanReferensi'),
			kecamatan: str(v, 'kecamatanReferensi'),
			city: str(v, 'kotaReferensi'),
			phoneNumber: str(v, 'noTeleponReferensi'),
		},
		environmentSurvey: {
			informationSourceName: str(v, 'namaSumberInformasi'),
			relationshipWithCustomer: str(v, 'hubunganSurvey'),
			mobilePhone: str(v, 'noHandphoneSurvey'),
			customerCharacter: int(v, 'karakterCustomer'),
		},
		mailingAddress: {
			copyAddressFrom: int(v, 'salinAlamatDari'),
			fullAddress: str(v, 'alamatLengkapMailing'),
			rt: str(v, 'rtMailing'),
			rw: str(v, 'rwMailing'),
			postalCode: str(v, 'kodePosMailing'),
			kelurahan: str(v, 'kelurahanMailing'),
			kecamatan: str(v, 'kecamatanMailing'),
			city: str(v, 'kotaMailing'),
			phoneNumber: str(v, 'noTeleponMailing'),
			distanceFromMtfOfficeKm: int(v, 'jarakDariKantorMtf'),
		},
		beneficialOwners: (
			(v['pemilikManfaatData'] as Record<string, unknown>[]) ?? []
		).map((bo) => ({
			relationshipWithCustomer: int(
				bo as unknown as Record<string, unknown>,
				'hubunganDenganCustomer',
			),
			name: (bo.nama as string) || null,
			nik: (bo.nik as string) || null,
			npwp: (bo.npwp as string) || null,
			birthPlace: (bo.tempatLahir as string) || null,
			birthDate:
				bo.tanggalLahir &&
				typeof bo.tanggalLahir === 'object' &&
				'format' in (bo.tanggalLahir as object)
					? (bo.tanggalLahir as dayjs.Dayjs).format('YYYY-MM-DD')
					: null,
			nationality:
				bo.kewarganegaraan != null
					? parseInt(String(bo.kewarganegaraan), 10)
					: null,
			originCountry: (bo.negaraAsal as string) || null,
			gender:
				bo.jenisKelamin != null ? parseInt(String(bo.jenisKelamin), 10) : null,
			maritalStatus:
				bo.statusPerkawinan != null
					? parseInt(String(bo.statusPerkawinan), 10)
					: null,
			postalCode: (bo.kodePos as string) || null,
			subDistrict: (bo.kelurahan as string) || null,
			district: (bo.kecamatan as string) || null,
			city: (bo.kota as string) || null,
			occupation: (bo.jenisPekerjaan as string) || null,
			companyOrBusinessName: (bo.namaPerusahaan as string) || null,
		})),
	};
};

export const mapFormToSaveAsset = (
	purchaseOrderNo: string,
	values: Record<string, unknown>,
): SaveQcPdAssetRequest => {
	const v = values;
	return {
		purchaseOrderNo,
		dealerSection: { dealer: str(v, 'assetDealer') },
		assetDetail: {
			assetDescription: str(v, 'assetDescription'),
			assemblyYear: int(v, 'assetTahunPerakitan'),
			manufacturingYear: int(v, 'assetManufacturingYear'),
			phJmbAmount: num(v, 'assetPhmjb'),
			otrAmount: num(v, 'assetHargaOtr') ?? num(v, 'assetOtrPrice'),
			otrIncludingKaroseriAmount: null,
			downPaymentAmount: num(v, 'assetUangMuka') ?? num(v, 'assetDownPayment'),
			downPaymentIncludingKaroseriAmount: null,
			assetCondition: str(v, 'assetKondisi') ?? str(v, 'assetUsedNew'),
			assetUsage: str(v, 'assetUsage'),
			category: int(v, 'assetKategori'),
			user: str(v, 'assetPengguna'),
			usageDescription: str(v, 'assetPenggunaan'),
			usageLocation: str(v, 'assetLokasiPenggunaan'),
			storageLocation: str(v, 'assetLokasiPenyimpanan'),
		},
		karoseriSelection: {
			supplierKaroseri: str(v, 'assetSupplierKaroseri'),
			karoseriItem: str(v, 'assetKaroseriItem'),
			itemPricePerUnitAmount: num(v, 'assetItemPricePerUnit'),
			itemQty: int(v, 'assetItemQty'),
			totalDownPaymentAmount: num(v, 'assetTotalDownPayment'),
			totalPriceKaroseriItemAmount: num(v, 'assetTotalPriceKaroseriItem'),
			totalDownPaymentAllKaroseriItemAmount: num(v, 'assetTotalDpAllKaroseri'),
		},
		assetRegistration: {
			bpkbOwnerName: str(v, 'assetNamaPemilikBpkb'),
			useCustomerAddress: bool(v, 'useCustomerAddress'),
			fullAddress: str(v, 'assetBpkbAlamat'),
			rt: str(v, 'assetBpkbRt'),
			rw: null,
			postalCode: str(v, 'assetBpkbKodePos'),
			newOwner: {
				newBpkbOwnerName: str(v, 'assetBpkbBaruNama'),
				customerTypeId: int(v, 'assetBpkbBaruCustomerType'),
				newBpkbOwnerOnBehalfOfId: int(v, 'assetBpkbBaruAtasNama'),
				identityNumber: str(v, 'assetBpkbBaruNoIdentitas'),
				fullAddress: str(v, 'assetBpkbBaruAlamat'),
				rt: str(v, 'assetBpkbBaruRt'),
				rw: null,
				postalCode: str(v, 'assetBpkbBaruKodePos'),
				businessEntityTypeId: int(v, 'assetBpkbBaruJenisBadanUsaha'),
				siuppNumber: str(v, 'assetBpkbBaruNoSiupp'),
				tdpNumber: str(v, 'assetBpkbBaruNoTdp'),
				deedNumber: str(v, 'assetBpkbBaruNoAkta'),
				npwpNumber: str(v, 'assetBpkbBaruNoNpwp'),
			},
		},
		assetInsurance: {
			insuredBy: str(v, 'assetDiasuransikanOleh'),
			insurancePaidBy: str(v, 'assetAsuransiDibayarOleh'),
		},
		showroomEmployee: {
			salesman: str(v, 'assetSalesman'),
			supplierAdmin: str(v, 'assetSupplierAdmin'),
			salesSupervisor: str(v, 'assetSalesSupervisor'),
			salesCoordinator: str(v, 'assetSalesCoordinator'),
		},
		tesEmployee: {
			accountOfficer: str(v, 'assetAccountOfficer'),
			surveyor: str(v, 'assetSurveyor'),
			creditAnalyst: str(v, 'assetCreditAnalyst'),
		},
		supplierEmployee: {
			salesman: str(v, 'assetSalesmanSp'),
			supplierAdmin: str(v, 'assetSupplierAdminSp'),
			salesSupervisor: str(v, 'assetSalesSupervisorSp'),
			salesCoordinator: str(v, 'assetSalesCoordinatorSp'),
		},
	};
};

export const mapFormToSaveFinancial = (
	purchaseOrderNo: string,
	values: Record<string, unknown>,
): SaveQcPdFinancialRequest => {
	const v = values;
	return {
		purchaseOrderNo,
		vehiclePriceAmount: num(v, 'hargaKendaraan'),
		principalDebtAmount: num(v, 'pokokHutang'),
		insurancePremiumAmount: num(v, 'premiAsuransi'),
		insurancePrepaidPaymentAmount: num(v, 'pembayaranAsuransiDiterimaDimuka'),
		insuranceAddedToPrincipalAmount: num(v, 'asuransiMenambahPokokHutang'),
		effectiveInterestPercentage: num(v, 'bungaEfektif'),
		dealerInterestSubsidyAmount: num(v, 'subsidiBungaDariDealer'),
		atpmInterestSubsidyAmount: num(v, 'subsidiBungaDariAtpm'),
		dealerCommissionAmount: num(v, 'komisiKeDealer'),
		dealerRiskSubsidyAmount: num(v, 'subsidiRiskDariDealer'),
		installmentPeriodCount: int(v, 'jumlahPeriodeAngsuran'),
		installmentValueAmount: num(v, 'nilaiAngsuran'),
		downPaymentAmount: num(v, 'uangMuka'),
		sellingInterestRatePercentage: num(v, 'sukuBungaJual'),
		ahmMotorSubsidyAmount: num(v, 'subsidiDariAhmMotor'),
		mtfSubsidyAmount: num(v, 'subsidiDariMtf'),
		firstInstallmentType: str(v, 'angsuranPertamaFinancial'),
	};
};

export const mapFormToSaveInsurance = (
	purchaseOrderNo: string,
	values: Record<string, unknown>,
): SaveQcPdInsuranceRequest => {
	const v = values;
	return {
		purchaseOrderNo,
		insuranceData: {
			insuredBy: str(v, 'insDiasuransikanOleh'),
			paidBy: str(v, 'insDibayarkanOleh'),
			applicationTypeId: int(v, 'insTipeAplikasi'),
		},
		insuranceEntry: {
			insuranceId: null,
			insurancePaymentId: null,
			insuranceTenorTypeId: null,
			insurancePaymentFullCashFullCashTypeId: null,
			insurancePaymentSchemeGiroTypeGiroTypeId: null,
			tjhAmount: num(v, 'insTjh'),
			isRiot: bool(v, 'insHuruHara'),
			isFlood: bool(v, 'insBanjir'),
			isEarthquake: bool(v, 'insGempa'),
			isTerrorism: bool(v, 'insTeroris'),
			driverPersonalAccident: num(v, 'insPaPengemudi'),
			totalPassenger: int(v, 'insJumlahPenumpang'),
			isGap: bool(v, 'insAsuransiGap'),
			isHic: bool(v, 'insAsuransiHospitalInCar'),
			insuranceDiscountPercentage: num(v, 'insDiscountAsuransi'),
			thirdPartyInsuranceSubsidyAmount: num(v, 'insSubsidiAsuransiPihakKetiga'),
		},
	};
};

export const mapFormToSaveIncentive = (
	purchaseOrderNo: string,
	values: Record<string, unknown>,
	distributionData: IncentiveMappingResult['distributionData'],
): SaveQcPdIncentiveRequest => {
	const v = values;
	return {
		purchaseOrderNo,
		showroomEmployees: {
			salesmanId: int(v, 'incSalesman'),
			supplierAdminId: int(v, 'incSupplierAdmin'),
			branchManagerId: int(v, 'incBranchManager'),
			salesSupervisorId: int(v, 'incSalesSupervisor'),
			salesCoordinatorId: int(v, 'incSalesCoordinator'),
		},
		mandiriEmployees: {
			mandiriEmployeeId: int(v, 'incMandiriEmployee'),
			mandiriBranchManagerId: int(v, 'incBmMandiri'),
			mandiriKpmNoId: int(v, 'incMandiriKpmNo'),
		},
		distributionItems: distributionData.map((item) => ({
			recipientType: item.penerima || null,
			employeeName: item.namaKaryawan || null,
			bankAccountNo: item.noRekening || null,
			bankAccountName: item.namaRekening || null,
			residualPremiumRefundAmount: item.sisaPremi,
			adminExcessRefundAmount: item.kelebihanAdmin,
			interestExcessRefundAmount: item.kelebihanBunga,
			provisionExcessRefundAmount: item.kelebihanProvisi,
		})),
		documentUpload: undefined,
	};
};

// ═══════════════════════════════════════════════════════════════════════════
// TAB FIELD NAMES — for per-tab validation
// ═══════════════════════════════════════════════════════════════════════════

export const TAB_FIELD_NAMES: Record<string, string[]> = {
	'data-customer': [
		'namaSesuaiAkta',
		'npwp',
		'namaDataBank',
		'alamatLengkap',
		'rt',
		'rw',
		'kodePos',
		'kelurahan',
		'kecamatan',
		'kota',
		'noTelepon',
		'tempatAktaDiterbitkan',
		'tahunPendirian',
		'jenisPerusahaan',
		'emailPerusahaan',
		'jumlahKaryawan',
		'groupBmpp',
		'targetMarket',
		'goPublic',
		'statusPerusahaan',
		'alamatLengkapAkta',
		'rtAkta',
		'rwAkta',
		'addressAkta',
		'noTeleponAkta',
		'noHandphoneAkta',
		'penghasilanPerBulan',
		'jenisRekeningBank',
		'statusTempatUsaha',
		'tanggalBerakhirnyaSewa',
		'statusKepemilikanGudang',
		'namaBank',
		'namaCabangBank',
		'nomorRekening',
		'setujuPemasaranPihakKetiga',
		'pernahKreditMobil',
		'daftarHitamAppi',
		'setujuPemrosesan',
		'setujuPemasaranMtf',
		'namaPicTtd',
		'alamatLengkapPicTtd',
		'noKtpPicTtd',
		'jabatanPicTtd',
		'namaPicPenagihan',
		'jabatanPicPenagihan',
		'noTeleponPicPenagihan',
		'noTelepon2PicPenagihan',
	],
	'application-data': [
		'productCustomerType',
		'mandiriKpm',
		'jenisAssetApp',
		'angsuranPertama',
		'spesifikProduct',
		'produk',
		'kondisiKendaraan',
		'tenorApp',
		'productOffering',
		'tahunPerakitan',
		'mklNo',
		'poNumber',
		'installmentScheme',
		'interestRate',
		'multipleAgreement',
		'copyAgreement',
		'jenisPembayaran',
		'tanggalSurvey',
		'sumberAplikasi',
		'namaBankApp',
		'bankBranch',
		'accountOfficer',
		'creditAnalyst',
		'surveyor',
		'fiducia',
		'admin',
		'provisi',
		'serviceFee',
		'refundAdministration',
		'refundProvisi',
		'namaReferensi',
		'hubunganReferensi',
		'alamatLengkapReferensi',
		'namaSumberInformasi',
		'hubunganSurvey',
		'noHandphoneSurvey',
		'karakterCustomer',
		'salinAlamatDari',
		'alamatLengkapMailing',
		'rtMailing',
		'kelurahanMailing',
		'kodePosMailing',
		'kecamatanMailing',
		'noTeleponMailing',
		'kotaMailing',
		'jarakDariKantorMtf',
	],
	'asset-data': [
		'assetDealer',
		'assetDescription',
		'assetTahunPerakitan',
		'assetPhmjb',
		'assetHargaOtr',
		'assetUangMuka',
		'assetKondisi',
		'assetUsage',
		'assetKategori',
		'assetOtrPrice',
		'assetDownPayment',
		'assetUsedNew',
		'assetManufacturingYear',
		'assetPengguna',
		'assetPenggunaan',
		'assetLokasiPenggunaan',
		'assetLokasiPenyimpanan',
		'assetNamaPemilikBpkb',
		'assetDiasuransikanOleh',
		'assetAsuransiDibayarOleh',
	],
	'insurance-data': [
		'insDiasuransikanOleh',
		'insDibayarkanOleh',
		'insTenorCredit',
		'insTipeAplikasi',
		'insNilaiPertanggungan',
		'insTipeAsuransi',
		'insPembayaranAsuransi',
		'insAsuransiTenor',
		'insTjh',
		'insHuruHara',
		'insBanjir',
		'insGempa',
		'insTeroris',
		'insPaPengemudi',
		'insJumlahPenumpang',
		'insAsuransiGap',
		'insAsuransiHospitalInCar',
		'insDiscountAsuransi',
		'insSubsidiAsuransiPihakKetiga',
	],
	'financial-data': [
		'hargaKendaraan',
		'pokokHutang',
		'premiAsuransi',
		'pembayaranAsuransiDiterimaDimuka',
		'asuransiMenambahPokokHutang',
		'bungaEfektif',
		'subsidiBungaDariDealer',
		'subsidiBungaDariAtpm',
		'komisiKeDealer',
		'subsidiRiskDariDealer',
		'jumlahPeriodeAngsuran',
		'nilaiAngsuran',
		'uangMuka',
		'sukuBungaJual',
		'subsidiDariAhmMotor',
		'subsidiDariMtf',
		'angsuranPertamaFinancial',
	],
	'incentive-data': [
		'incNamaDealer',
		'incNamaCustomer',
		'incSalesman',
		'incSupplierAdmin',
		'incBranchManager',
		'incSalesSupervisor',
		'incSalesCoordinator',
		'incMandiriEmployee',
		'incBmMandiri',
		'incMandiriKpmNo',
	],
};
